/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiButton } from '../../button';
import { EuiI18n } from '../../i18n';
import { EuiToolTip } from '../../tool_tip';
export class EuiSuperUpdateButton extends Component {
  static defaultProps = {
    needsUpdate: false,
    isLoading: false,
    isDisabled: false,
    showTooltip: false
  };
  _isMounted = false;
  tooltip = null;

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate() {
    if (this.props.showTooltip && this.props.needsUpdate && !this.props.isDisabled && !this.props.isLoading) {
      this.showTooltip();
      this.tooltipTimeout = setTimeout(() => {
        this.hideTooltip();
      }, 2000);
    }
  }

  setTootipRef = node => {
    this.tooltip = node;
  };
  showTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }

    this.tooltip.showToolTip();
  };
  hideTooltip = () => {
    if (!this._isMounted || !this.tooltip) {
      return;
    }

    this.tooltip.hideToolTip();
  };

  render() {
    const {
      className,
      needsUpdate,
      isLoading,
      isDisabled,
      onClick,
      toolTipProps,
      showTooltip,
      ...rest
    } = this.props;
    const classes = classNames('euiSuperUpdateButton', className);
    let buttonText = <EuiI18n token="euiSuperUpdateButton.refreshButtonLabel" default="Refresh" />;

    if (needsUpdate || isLoading) {
      buttonText = isLoading ? <EuiI18n token="euiSuperUpdateButton.updatingButtonLabel" default="Updating" /> : <EuiI18n token="euiSuperUpdateButton.updateButtonLabel" default="Update" />;
    }

    let tooltipContent;

    if (isDisabled) {
      tooltipContent = <EuiI18n token="euiSuperUpdateButton.cannotUpdateTooltip" default="Cannot update" />;
    } else if (needsUpdate && !isLoading) {
      tooltipContent = <EuiI18n token="euiSuperUpdateButton.clickToApplyTooltip" default="Click to apply" />;
    }

    return <EuiToolTip ref={this.setTootipRef} content={tooltipContent} position="bottom" {...toolTipProps}>
        <EuiButton className={classes} color={needsUpdate || isLoading ? 'secondary' : 'primary'} fill iconType={needsUpdate || isLoading ? 'kqlFunction' : 'refresh'} textProps={{
        className: 'euiSuperUpdateButton__text'
      }} isDisabled={isDisabled} onClick={onClick} isLoading={isLoading} {...rest}>
          {buttonText}
        </EuiButton>
      </EuiToolTip>;
  }

}
EuiSuperUpdateButton.propTypes = {
  className: PropTypes.string,
  isDisabled: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  needsUpdate: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,

  /**
     * Passes props to `EuiToolTip`
     */
  toolTipProps: PropTypes.any,

  /**
     * Show the "Click to apply" tooltip
     */
  showTooltip: PropTypes.bool.isRequired
};

try {
  // @ts-ignore
  EuiSuperUpdateButton.displayName = "EuiSuperUpdateButton"; // @ts-ignore

  EuiSuperUpdateButton.__docgenInfo = {
    "description": "",
    "displayName": "EuiSuperUpdateButton",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "isDisabled": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isDisabled",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "isLoading": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "isLoading",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "needsUpdate": {
        "defaultValue": {
          value: false
        },
        "description": "",
        "name": "needsUpdate",
        "required": false,
        "type": {
          "name": "boolean"
        }
      },
      "onClick": {
        "defaultValue": null,
        "description": "",
        "name": "onClick",
        "required": true,
        "type": {
          "name": "(event: MouseEvent<HTMLButtonElement, MouseEvent>) => void"
        }
      },
      "toolTipProps": {
        "defaultValue": null,
        "description": "Passes props to `EuiToolTip`",
        "name": "toolTipProps",
        "required": false,
        "type": {
          "name": "Props | undefined"
        }
      },
      "showTooltip": {
        "defaultValue": {
          value: false
        },
        "description": "Show the \"Click to apply\" tooltip",
        "name": "showTooltip",
        "required": false,
        "type": {
          "name": "boolean"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/date_picker/super_date_picker/super_update_button.tsx#EuiSuperUpdateButton"] = {
      docgenInfo: EuiSuperUpdateButton.__docgenInfo,
      name: "EuiSuperUpdateButton",
      path: "src/components/date_picker/super_date_picker/super_update_button.tsx#EuiSuperUpdateButton"
    };
} catch (__react_docgen_typescript_loader_error) {}